/**
 * Uygulama kabugu — kenar cubugu, ust cubuk, icerik.
 *
 * ── UC KIRILIM, UC FARKLI GEZINME ────────────────────────────────────────────
 *   >= 1024px  kalici kenar cubugu (masaustu — Cortex ile ayni his)
 *   640–1023   cekmece (tablet — ekran alani degerli)
 *   < 640px    ALT gezinme cubugu (mobil — bas parmak erisimi)
 *
 * Mobilde ustte hamburger menu YERINE altta cubuk secildi: telefon tek elle
 * tutulurken ekranin ust kosesi en zor erisilen yerdir. Sahada, elinde tablet
 * veya telefonla ekran ayarlayan biri icin bu fark somut.
 *
 * ── MENU ICERIGI SUNUCUDAN ───────────────────────────────────────────────────
 * `me.nav` sunucudan gelir. Istemci "bu kullanici admin mi" hesabi yapmaz.
 */

import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { localizeCatalogName, useI18n, type TranslationKey } from '@/lib/i18n';
import { quoteRequestPath } from '@/lib/quote';
import { catalogNameLanguage, compareCatalogDisplayOrder, formatNumber, slugifyTr } from '@/lib/format';
import { Button } from '@/components/ui';
import { BrandLockup } from '@/components/BrandLockup';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import type { NavKey } from '@shared/index';
import './AppShell.css';

interface NavItem {
  key: string;
  gate: NavKey;
  to: string;
  label: TranslationKey;
  icon: JSX.Element;
  group: 'daily' | 'content' | 'operation' | 'account';
  end?: boolean;
}

/* Cizilmis ikonlar — masaustu sidebar'iyla ayni dil: tek cizgi agirligi, 16 izgara.
   Metin glifleri (◆ ▦ ⚙) fontlar arasi tutarsiz cizilir; SVG her yerde ayni. */
const NAV_ICONS = {
  home: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M2.5 6.8 8 2.2l5.5 4.6V13a1 1 0 0 1-1 1H9.8v-3.6H6.2V14H3.5a1 1 0 0 1-1-1V6.8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  library: (
    /* Pusula — "Kesfet": sayfa bir kitaplik degil, kesif yuzeyi. */
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.6 5.4-1.5 3.7-3.7 1.5 1.5-3.7 3.7-1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  operations: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.6V8l2.4 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  account: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5.2" r="2.6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.8 13.6a5.3 5.3 0 0 1 10.4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  subscriptions: (
    /* Kart + onay: "neye sahibim, ne kadar kaldi". */
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <rect x="1.8" y="3.5" width="12.4" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M1.8 6.6h12.4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4.6 9.8h3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M2 11.5h12M2 8h12M2 4.5h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="5.5" cy="4.5" r="1.4" fill="var(--surface-1)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10.5" cy="8" r="1.4" fill="var(--surface-1)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6.5" cy="11.5" r="1.4" fill="var(--surface-1)" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
} satisfies Record<NavKey, JSX.Element>;

/** Sira ANLAMLI: en sik kullanilandan en seyrek kullanilana.
    "Kitaplik" -> "Kesfet": sayfa "benim kutuphanem" degil bir kesif yuzeyi
    (kullanici geri bildirimi, 19 Agu 2026). Sahip olunan kategoriler kenar
    cubugunda ayri baglantilar olarak acilir. */
const NAV_ITEMS: NavItem[] = [
  { key: 'home', gate: 'home', to: '/', label: 'nav.home', icon: NAV_ICONS.home, group: 'daily', end: true },
  { key: 'library', gate: 'library', to: '/kesfet', label: 'nav.explore', icon: NAV_ICONS.library, group: 'daily' },
  { key: 'screens', gate: 'operations', to: '/operasyon/ekranlar', label: 'nav.devices', icon: NAV_ICONS.admin, group: 'operation' },
  { key: 'calendar', gate: 'operations', to: '/operasyon/takvim', label: 'nav.schedule', icon: NAV_ICONS.operations, group: 'operation' },
  { key: 'led-ads', gate: 'operations', to: '/operasyon/led-reklamlari', label: 'nav.ledAds', icon: NAV_ICONS.admin, group: 'operation' },
  { key: 'screen-studio', gate: 'operations', to: '/operasyon/ekran-studyo', label: 'nav.screenStudio', icon: NAV_ICONS.admin, group: 'operation' },
  { key: 'reports', gate: 'operations', to: '/raporlar', label: 'nav.reports', icon: NAV_ICONS.library, group: 'operation' },
  { key: 'subscriptions', gate: 'subscriptions', to: '/abonelikler', label: 'nav.subscription', icon: NAV_ICONS.subscriptions, group: 'account' },
  { key: 'support', gate: 'account', to: '/destek', label: 'nav.support', icon: NAV_ICONS.account, group: 'account' },
  { key: 'account', gate: 'account', to: '/hesap', label: 'nav.settings', icon: NAV_ICONS.admin, group: 'account' },
];

function creditColor(catalog: string): string {
  if (catalog === 'Leds Play') return '#22c7df';
  if (catalog === 'VR Deneyimleri') return '#6c8cff';
  if (catalog === 'AR Deneyimleri') return '#38c98b';
  return '#8b6cf0';
}

/** Monogram: "E2E Müşteri" -> "EM". Avatar gorseli olmayan hesabin yuzu. */
export function initials(name: string | undefined): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (a + b).toLocaleUpperCase('tr') || '·';
}

export default function AppShell() {
  const { me, logout, can } = useAuth();
  const { preference, setPreference, appearance } = useTheme();
  const { language, locale, t } = useI18n();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [activeCredit, setActiveCredit] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  /* Ust cubuk aramasi — Epic'in "Magazada ara" hapi. Gonderim Kesfet'e
     dusurur; sayfa `q` parametresini URL'den okudugu icin sonuc aninda gelir. */
  const [searchTerm, setSearchTerm] = useState('');
  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const q = searchTerm.trim();
    navigate(q ? `/kesfet?q=${encodeURIComponent(q)}` : '/kesfet');
    setSearchTerm('');
  }

  /**
   * ── KALICI KENAR CUBUGU `inert` OLMAMALI ─────────────────────────────────
   * `inert` cekmece kapaliyken klavye odagini disarida tutmak icindi; ama
   * kosul yalnizca `drawerOpen`a bakiyordu ve >=1024px'te kenar cubugu
   * KALICI oldugu halde inert kaliyordu — gorunen ama TIKLANAMAYAN sidebar
   * (sahada raporlandi, 19 Agu 2026). inert yalnizca cekmece kipinde gecerli.
   */
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Sayfa degisince cekmece kapanir. Aksi halde kullanici hedefe varir ama
  // ustunde hala menu durur.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Sunucunun capability listesini genişletmeyiz. Özellikle customer_admin
  // rolü tek başına ödeme/endpoint öncesi operasyon ekranlarını açamaz.
  const items = NAV_ITEMS.filter((item) => can(item.gate));
  /* Eski VR Oyunlar mock cüzdanı kanonik VR Deneyimleri aboneliğinin parçası
     değildir; ürünleri silindikten sonra kabukta ikinci bir bakiye üretmez. */
  const creditWallets = (me?.credits ?? [])
    .filter((wallet) => wallet.catalog !== 'VR Oyunlar' && wallet.catalog !== 'Servisler')
    .sort((a, b) => compareCatalogDisplayOrder(a.catalog, b.catalog, locale));
  const activeWallet = creditWallets.find((wallet) => wallet.catalog === activeCredit)
    ?? creditWallets[0];
  // Katalog çalışma alanı yalnız YÜRÜRLÜKTEKİ ürün yetkisinden doğar.
  // Onboarding onayı bir satış/abonelik değildir; onu buraya katmak, yetki
  // iptalinden sonra eski hizmetin menüde ve doğrudan URL'de kalmasına yol açar.
  const ownedCategories = [...new Set([
    ...(me?.entitlements ?? []).map((entitlement) => entitlement.category),
  ])]
    .filter((category) => category !== 'VR Oyunlar' && category !== 'Servisler')
    .sort((a, b) => compareCatalogDisplayOrder(a, b, locale));

  const brandStyle = me?.account.branding.accentColor ? {
    '--primary': me.account.branding.accentColor,
    '--primary-hover': `color-mix(in srgb, ${me.account.branding.accentColor} 86%, white)`,
    '--primary-active': `color-mix(in srgb, ${me.account.branding.accentColor} 84%, black)`,
    '--primary-subtle': `color-mix(in srgb, ${me.account.branding.accentColor} 16%, transparent)`,
  } as CSSProperties : undefined;

  return (
    <div className="shell" style={brandStyle}>
      <a className="skip-link" href="#main">
        {t('common.skipToContent')}
      </a>

      {/* ── Kenar cubugu / cekmece ─────────────────────────────────────────── */}
      <aside
        className={`shell__side ${drawerOpen ? 'is-open' : ''}`}
        // Cekmece KIPINDEYKEN ve kapaliyken klavye odagi icine girmemeli;
        // kalici kenar cubugu (>=1024px) her zaman etkilesimli.
        {...(isDesktop || drawerOpen ? {} : { inert: '' as unknown as boolean })}
      >
        <div className="shell__brand">
          <BrandLockup className="shell__brandlockup" />
        </div>

        <nav className="shell__nav" aria-label={t('nav.main')}>
          {(['daily', 'operation', 'account'] as const).map((group) => {
            const groupItems = items.filter((item) => item.group === group);
            if (groupItems.length === 0) return null;
            const label = group === 'daily' ? null : group === 'operation' ? t('nav.operations') : t('nav.account');
            return (
              <Fragment key={group}>
              <div className="shell__navgroup">
                {label && <div className="shell__groupname">{label}</div>}
                {groupItems.map((item) => (
                  <NavLink key={item.key} to={item.to} {...(item.end ? { end: true } : {})} className={({ isActive }) => `shell__link ${isActive ? 'is-active' : ''}`}>
                    <span className="shell__icon" aria-hidden="true">{item.icon}</span>
                    {t(item.label)}
                    {item.key === 'subscriptions' && (me?.pendingOfferCount ?? 0) > 0 && (
                      <span className="shell__navbadge" aria-label={`${me!.pendingOfferCount} yeni teklif`}>
                        {Math.min(me!.pendingOfferCount, 9)}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
              {group === 'daily' && ownedCategories.length > 0 && (
                <div className="shell__navgroup shell__navgroup--catalogs">
                  <div className="shell__groupname">{t('nav.yourCatalogs')}</div>
                  {ownedCategories.map((category) => (
                    <NavLink key={category} to={`/katalog/${slugifyTr(category)}`} className={({ isActive }) => `shell__link ${isActive ? 'is-active' : ''}`}>
                      <span className="shell__icon" aria-hidden="true">{NAV_ICONS.admin}</span>
                      <span className="shell__catlabel" lang={catalogNameLanguage(category)}>{localizeCatalogName(category, language)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
              </Fragment>
            );
          })}
        </nav>

        {creditWallets.length > 0 && activeWallet && (
          <div className={`shell__creditdock ${creditOpen ? 'is-open' : ''}`}>
            <div className="shell__creditdockhead">
              <Link to="/abonelikler">{t('nav.catalogCredits')}</Link>
              <button
                type="button"
                aria-expanded={creditOpen}
                aria-controls="catalog-credit-popover"
                aria-label={creditOpen ? 'Kredi ayrıntılarını kapat' : 'Kredi ayrıntılarını aç'}
                onClick={() => setCreditOpen((open) => !open)}
              >
                <span aria-hidden="true">{creditOpen ? '▴' : '▾'}</span>
              </button>
            </div>
            <div className="shell__creditrings" aria-label={t('nav.catalogCredits')}>
              {creditWallets.map((wallet) => {
                const ratio = wallet.granted > 0
                  ? Math.max(0, Math.min(1, wallet.balance / wallet.granted))
                  : 0;
                const selected = activeWallet.catalog === wallet.catalog;
                return (
                  <button
                    type="button"
                    className={`shell__creditring ${selected ? 'is-active' : ''}`}
                    key={wallet.catalog}
                    aria-label={`${localizeCatalogName(wallet.catalog, language)}: ${formatNumber(wallet.balance)} kredi`}
                    aria-expanded={selected && creditOpen}
                    onMouseEnter={() => setActiveCredit(wallet.catalog)}
                    onFocus={() => setActiveCredit(wallet.catalog)}
                    onClick={() => {
                      setActiveCredit(wallet.catalog);
                      setCreditOpen(selected ? !creditOpen : true);
                    }}
                    style={{
                      ['--credit-color' as string]: creditColor(wallet.catalog),
                      ['--credit-ratio' as string]: ratio,
                    }}
                  >
                    <span className="shell__creditringcore">
                      <strong className="num">{formatNumber(wallet.balance)}</strong>
                    </span>
                  </button>
                );
              })}
            </div>

            <aside
              className="shell__creditpopover"
              id="catalog-credit-popover"
              aria-label={`${localizeCatalogName(activeWallet.catalog, language)} kredi ayrıntısı`}
              style={{ ['--credit-color' as string]: creditColor(activeWallet.catalog) }}
            >
              <div className="shell__creditpophead">
                <span className="shell__creditpopdot" aria-hidden="true" />
                <strong lang={catalogNameLanguage(activeWallet.catalog)}>{localizeCatalogName(activeWallet.catalog, language)}</strong>
              </div>
              <div className="shell__creditbalance">
                <strong className="num">{formatNumber(activeWallet.balance)}</strong>
                <span>{language === 'tr' ? 'kredi kaldı' : 'credits remaining'}</span>
              </div>
              <dl className="shell__creditstats">
                <div><dt>{language === 'tr' ? 'Yüklenen' : 'Granted'}</dt><dd className="num">{formatNumber(activeWallet.granted)}</dd></div>
                <div><dt>{language === 'tr' ? 'Kullanılan' : 'Used'}</dt><dd className="num">{formatNumber(Math.max(0, activeWallet.granted - activeWallet.balance))}</dd></div>
              </dl>
              <div className="shell__creditpopbar" aria-hidden="true">
                <span style={{ ['--p' as string]: activeWallet.granted > 0 ? Math.max(0, Math.min(1, activeWallet.balance / activeWallet.granted)) : 0 }} />
              </div>
              <Link to="/abonelikler" onClick={() => setCreditOpen(false)}>
                {language === 'tr' ? 'Abonelik ayrıntısını aç' : 'Open subscription details'} <span aria-hidden="true">→</span>
              </Link>
            </aside>
          </div>
        )}

        {/* Kurum kimligi sidebar'in dibinde — masaustu footer'inin karsiligi.
            Ust cubukta kurum adi tasimak "kim oldugum" bilgisini gezinmeden
            ayirir; burada marka ile ayni kolonda durur. */}
        {me && (
          <Link to="/hesap" className="shell__org" aria-label={t('nav.accountAndOrg')}>
            {me.account.branding.logoUrl && <img className="shell__orglogo" src={me.account.branding.logoUrl} alt="" />}
            <div className="shell__orgname" title={me.account.name}>
              {me.account.branding.displayName || me.account.name}
            </div>
            <div className="shell__orgsub">{t('common.accountSettings')} →</div>
          </Link>
        )}
      </aside>

      {drawerOpen && (
        <button
          type="button"
          className="shell__scrim"
          onClick={() => setDrawerOpen(false)}
          aria-label={t('nav.closeMenu')}
        />
      )}

      {/* ── Ust cubuk ──────────────────────────────────────────────────────── */}
      <header className="shell__top">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="shell__menubtn"
          onClick={() => setDrawerOpen((v) => !v)}
          aria-label={t('nav.openMenu')}
          aria-expanded={drawerOpen}
        >
          <MenuIcon />
        </Button>

        {can('library') && (
          <form className="shell__search" role="search" onSubmit={submitSearch}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.5" />
              <path d="m10.6 10.6 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              className="shell__searchinput"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('common.searchCatalog')}
              aria-label={t('common.searchCatalog')}
            />
          </form>
        )}

        <div className="shell__topactions">
          <LanguageSwitch />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={() =>
              setPreference(preference === 'dark' ? 'light' : preference === 'light' ? 'system' : 'dark')
            }
            aria-label={`${t('common.theme')}: ${preference === 'dark' ? t('common.themeDark') : preference === 'light' ? t('common.themeLight') : t('common.themeSystem')}. ${t('common.changeTheme')}`}
            title={`${t('common.theme')}: ${preference === 'dark' ? t('common.themeDark') : preference === 'light' ? t('common.themeLight') : t('common.themeSystem')}`}
          >
            <ThemeIcon mode={preference === 'system' ? 'system' : appearance} />
          </Button>

          <NotificationBell />

          <div className="shell__user">
            <Link to="/hesap" className="shell__userlink" aria-label={t('common.accountSettings')}>
              <span className="shell__avatar" aria-hidden="true">
                {initials(me?.user.name)}
              </span>
              <span className="shell__username">{me?.user.name}</span>
            </Link>
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              {t('common.signOut')}
            </Button>
          </div>
        </div>
      </header>

      <main className="shell__main" id="main" tabIndex={-1}>
        <div className="shell__content">
          <TrialBanner />
          <Outlet />
        </div>
      </main>

      {/* ── Mobil alt gezinme ──────────────────────────────────────────────── */}
      <nav className="shell__bottom" aria-label={t('nav.bottom')}>
        {items.slice(0, 5).map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `shell__bottomlink ${isActive ? 'is-active' : ''}`}
          >
            <span className="shell__icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="shell__bottomlabel">{t(item.label)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true">
      <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ThemeIcon({ mode }: { mode: 'dark' | 'light' | 'system' }) {
  if (mode === 'system') {
    return (
      <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 3a7 7 0 0 1 0 14Z" fill="currentColor" />
      </svg>
    );
  }
  if (mode === 'dark') {
    return (
      <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true">
        <path d="M15.8 12.4A6.7 6.7 0 0 1 7.6 4.2 6.8 6.8 0 1 0 15.8 12.4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="3.2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.3 4.3l1.4 1.4M14.3 14.3l1.4 1.4M15.7 4.3l-1.4 1.4M5.7 14.3l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function trialCountdown(daysLeft: number, language: 'tr' | 'en'): string {
  if (language === 'en') return daysLeft === 0 ? 'ends today' : daysLeft === 1 ? 'ends tomorrow' : `ends in ${daysLeft} days`;
  return daysLeft === 0 ? 'bugün bitiyor' : daysLeft === 1 ? 'yarın bitiyor' : `${daysLeft} gün sonra bitiyor`;
}

/**
 * Bildirim zili.
 *
 * ── NEDEN ZIL ────────────────────────────────────────────────────────────────
 * Deneme geri sayimi her sayfada kalici bir serit olarak duruyordu; kullanici
 * bunu gurultu olarak raporladi (19 Agu 2026). Ambiyans bilgisi zile tasindi:
 * rozet sayiyi soyler, acilir panel detayi verir. Kalici serit yalnizca
 * ACIL durumda (son 3 gun) geri gelir — bkz. TrialBanner.
 */
function NotificationBell() {
  const { me } = useAuth();
  const { language, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const trials = me?.trials ?? [];
  const count = trials.length;

  /* "Okundu" durumu — panel acildiginda o anki bildirimler gorulmus sayilir
     ve rozet soner. Imza gun sayisini icerir: yeni gun = yeni bilgi = rozet
     geri gelir. Sunucuda okundu kaydi yok; localStorage yeterli. */
  const sigs = trials.map((t) => `${t.entitlementId}:${t.daysLeft}`);
  const [seen, setSeen] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('cortex.bell.seen') ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const unseen = sigs.filter((s) => !seen.includes(s)).length;

  function toggleOpen() {
    if (!open) {
      setSeen(sigs);
      try {
        localStorage.setItem('cortex.bell.seen', JSON.stringify(sigs));
      } catch {
        /* localStorage kapali olabilir; rozet sadece oturum boyunca soner. */
      }
    }
    setOpen((v) => !v);
  }

  return (
    <div className="bell" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={unseen > 0 ? `${t('nav.notifications')}, ${unseen} ${language === 'tr' ? 'yeni' : 'new'}` : t('nav.notifications')}
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
          <path
            d="M8 2a4 4 0 0 1 4 4v2.4l1.2 2.1a.6.6 0 0 1-.52.9H3.32a.6.6 0 0 1-.52-.9L4 8.4V6a4 4 0 0 1 4-4Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M6.6 13.7a1.5 1.5 0 0 0 2.8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </Button>
      {unseen > 0 && (
        <span className="bell__badge" aria-hidden="true">
          {unseen}
        </span>
      )}

      {open && (
        <div className="bell__panel" role="dialog" aria-label={t('nav.notifications')}>
          <div className="bell__head">{t('nav.notifications')}</div>
          {count === 0 ? (
            <div className="bell__empty">{t('nav.noNotifications')}</div>
          ) : (
            <>
              {trials.map((trial) => (
                <div key={trial.entitlementId} className={`bell__item ${trial.endingSoon ? 'is-urgent' : ''}`}>
                  <span className="bell__dot" aria-hidden="true" />
                  <div className="bell__body">
                    <div>
                      <strong>{trial.packageName}</strong> {t('nav.trialEnds').toLocaleLowerCase(localeFor(language))} {trialCountdown(trial.daysLeft, language)}.
                    </div>
                    <div className="bell__sub">
                      {t('nav.trialAccessNotice')}
                    </div>
                  </div>
                </div>
              ))}
              <Link className="bell__foot" to={quoteRequestPath({ solution: language === 'tr' ? 'Deneme sonrası devam' : 'Continue after trial', source: '/' })} onClick={() => setOpen(false)}>
                {t('nav.contact')}
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Deneme geri sayimi — yalnizca ACIL olanlar (son 3 gun).
 *
 * Serit her denemede her sayfada duruyordu; kullanici gurultu olarak
 * raporladi (19 Agu 2026). Ambiyans bilgisi artik zil panelinde
 * (NotificationBell). Denemenin sessizce bitmemesi garantisi korunuyor:
 * son 3 gunde serit geri gelir ve renk uyariya doner.
 */
function TrialBanner() {
  const { me } = useAuth();
  const { language, t } = useI18n();
  const trials = (me?.trials ?? []).filter((t) => t.endingSoon);
  if (trials.length === 0) return null;

  return (
    <div className="stack stack--sm" style={{ marginBottom: 'var(--space-5)' }}>
      {trials.map((trial) => (
        <div
          key={trial.entitlementId}
          className={`trial-strip ${trial.endingSoon ? 'is-urgent' : ''}`}
          role={trial.endingSoon ? 'alert' : 'status'}
        >
          <span className="trial-strip__dot" aria-hidden="true" />
          <span>
            <strong>{trial.packageName}</strong> {t('nav.trialEnds').toLocaleLowerCase(localeFor(language))}{' '}
            {trialCountdown(trial.daysLeft, language)}
            .{' '}
            {trial.endingSoon
              ? t('nav.contactForContinuity')
              : t('nav.trialAccessNotice')}
          </span>
          <Link className={`btn btn--sm ${trial.endingSoon ? 'btn--primary' : 'btn--secondary'}`} to={quoteRequestPath({ solution: `${trial.packageName} — ${language === 'tr' ? 'Deneme sonrası devam' : 'Continue after trial'}`, source: '/' })}>{t('nav.getQuote')}</Link>
        </div>
      ))}
    </div>
  );
}

function localeFor(language: 'tr' | 'en'): string {
  return language === 'tr' ? 'tr-TR' : 'en-US';
}
